import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Button,
  FormControlLabel,
  FormControl,
  Checkbox,
  CircularProgress,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import './DownloadForm.css';
import { showInCurrentEnvironment } from 'utils/commonTools';
import { translation } from 'utils/translate';
import { useSelector } from 'react-redux';
import apiService from 'services/apiService';

const tabs = [
  'meta',
  'content'
];

const fileFormats = [
  { key: 'csv', label: 'CSV' },
  { key: 'xlsx', label: 'Excel' },
];

const downloadableFileFormats = [
  'orig',
  'text',
  'xml',
];

const defaultMetadataFormat = 'csv';

function CircularProgressWithLabel({ value, ...props }) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress
        variant="determinate"
        value={value}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography
          variant="caption"
          component="div"
          color="textSecondary"
        >
          {`${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const DownloadForm = ({
  loadingDataFromBackend,
  loadingPercent,
  handleDownload,
  fileDownloadLink,
  handleDownloadZippedFile,
  downloadWarnings,
}) => {
  const [activeTab, setActiveTab] = useState('meta');
  const [metaFormat, setMetaFormat] = useState(
    defaultMetadataFormat,
  );
  const [selectedFileFormats, setSelectedFileFormats] =
    useState([]);
  const [hasAcceptedTerms, setHasAcceptedTerms] =
    useState(false);

  const lang = useSelector((store) => store.lang);

  const toggleMetaFormat = (event) => {
    setMetaFormat(event.target.value);
  };

  // toggle individual checkbox
  const toggleFileFormat = (event) => {
    const { checked, value } = event.target;
    setSelectedFileFormats(
      checked
        ? [...selectedFileFormats, value]
        : selectedFileFormats.filter(
          (item) => item !== value,
        ),
    );
  };

  const toggleAcceptTerms = ({ target }) => {
    // fallback to false on nullish
    setHasAcceptedTerms(target?.checked ?? false);
  };

  const renderAcceptTermsCheckbox = () => (
    <>
      <FormControlLabel
        required
        checked={hasAcceptedTerms}
        onChange={toggleAcceptTerms}
        label={
          <a
            href={`https://lakitutka.fi/tutkijalle?lang=${lang}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {translation('download', 'terms', lang)}
          </a>
        }
        control={<Checkbox />}
      />
      <br /> {/* quick fix for missing newline */}
    </>
  );

  const renderMetadataTools = () => (
    <>
      {loadingDataFromBackend && (
        <CircularProgress
          size={68}
          className="circular__progress__centered"
        />
      )}
      <FormControl
        component="fieldset"
        className="download-form__fieldset"
      >
        <h4 className="download-form__subtitle">
          {translation("download", "format", lang)}
        </h4>
        <RadioGroup
          onChange={toggleMetaFormat}
          className="radio-group-format"
        >
          {fileFormats.map(({ key, label }) => (
            <FormControlLabel
              key={`form-control-label-format-${key}`}
              control={
                <Radio
                  checked={metaFormat === key}
                  value={key}
                  name="file-format"
                />
              }
              label={label}
            />
          ))}
        </RadioGroup>
      </FormControl>
      <div className="download-form__button-wrapper">
        {renderAcceptTermsCheckbox()}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={
            loadingDataFromBackend || !hasAcceptedTerms
          }
          onClick={() => handleDownload({ metaFormat })}
        >
          {translation("download", "meta", lang)}
        </Button>
      </div>
    </>
  );

  const downloadPreparedFile = async () => {
    try {
      const archive = await apiService.getZippedFile(
        fileDownloadLink,
      );
      const objectUrl = window.URL.createObjectURL(archive);
      const link = document.createElement('a');

      link.href = objectUrl;
      link.download = `${fileDownloadLink}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 0);
      handleDownloadZippedFile();
    } catch (error) {
      // eslint-disable-next-line no-alert
      window.alert(error.message);
    }
  };

  const renderContentTools = () => (
    <>
      {fileDownloadLink && downloadWarnings.length > 0 && (
        <Alert severity="warning">
          {translation('download', 'missing_documents', lang)}
          <ul>
            {downloadWarnings.map(({ document }, index) => (
              <li key={`${document}-${index}`}>{document}</li>
            ))}
          </ul>
        </Alert>
      )}
      <FormControl
        component="fieldset"
        className="download-form__fieldset"
      >
        <h4 className="download-form__subtitle">
          {translation("download", "formats", lang)}
        </h4>
        {downloadableFileFormats
          .filter(showInCurrentEnvironment)
          .map(key => (
            // same as <> but allows key attribute
            <Fragment key={key}>
              <FormControlLabel
                control={
                  <Checkbox
                    className="download-form__checkbox"
                    value={key}
                    checked={selectedFileFormats.includes(
                      key,
                    )}
                    onChange={toggleFileFormat}
                    name="document-meta-checkbox"
                    color="primary"
                  />
                }
                label={translation("download", "file_formats", key, "label", lang)}
              />
              <div className="checkbox-info">
                {translation("download", "file_formats", key, "info", lang)}
              </div>
            </Fragment>
          ))}
      </FormControl>
      <div>
        {fileDownloadLink ? (
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={downloadPreparedFile}
          >
            {translation("download", "content", lang)}
          </Button>
        ) : loadingDataFromBackend ? (
          <div>
            {translation("download", "gathering", lang)}
            <CircularProgressWithLabel
              value={loadingPercent}
            />
          </div>
        ) : (
          <div className="download-form__button-wrapper">
            {renderAcceptTermsCheckbox()}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                !selectedFileFormats.length ||
                !hasAcceptedTerms
              }
              onClick={() =>
                handleDownload({
                  fileFormats: selectedFileFormats,
                })
              }
            >
              {translation("download", "prepare", lang)}
            </Button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <Paper elevation={2} className="download-form">
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <h4 className="download-form__title">
            {translation("download", "title", lang)}
          </h4>
        </Grid>

        <Grid item xs={12}>
          {tabs.map(key => (
            <button
              key={key}
              type="button"
              className={
                activeTab === key
                  ? 'document-type-select document-type-select--active'
                  : 'document-type-select'
              }
              onClick={() => {
                // change tab and reset terms
                setHasAcceptedTerms(false);
                setActiveTab(key);
              }}
              disabled={loadingDataFromBackend}
            >
              {translation("download", "tabs", key, lang)}
            </button>
          ))}
          <div className="download-form__inner">
            {activeTab === 'meta' && renderMetadataTools()}
            {activeTab === 'content' &&
              renderContentTools()}
          </div>
        </Grid>
      </Grid>
    </Paper>
  );
};

CircularProgressWithLabel.propTypes = {
  value: PropTypes.number.isRequired,
};

DownloadForm.defaultProps = {
  downloadWarnings: [],
};

DownloadForm.propTypes = {
  downloadWarnings: PropTypes.arrayOf(PropTypes.shape({
    document: PropTypes.string.isRequired,
    reason: PropTypes.string.isRequired,
  })),
  loadingDataFromBackend: PropTypes.bool.isRequired,
  loadingPercent: PropTypes.number.isRequired,
  handleDownload: PropTypes.func.isRequired,
  fileDownloadLink: PropTypes.string.isRequired,
  handleDownloadZippedFile: PropTypes.func.isRequired,
};

export default DownloadForm;
